import { CommitService } from '../../commit/CommitService';
import { SessionService } from '../../session/session.service';
import { OperationContext, OperationSpec } from '../models/OperationContext';
import { OperationStatus } from '../models/RunnableOperation';
import { TransactionBlock } from '../models/TransactionBlock';
import { EventEmitter } from '../utils/EventEmitter';
import { OperationCompiler } from './OperationCompiler';

export class TransactionManager extends EventEmitter {
  private transactionBlocks: TransactionBlock[] = [];
  private compiler: OperationCompiler;

  private isProcessing: boolean = false;

  constructor(compiler: OperationCompiler) {
    super();
    this.compiler = compiler;
  }

  addOperation(spec: Partial<OperationSpec>): string {
    const operationId = this.generateOperationId();
    spec.operationId = operationId;
    const operations = this.compiler.compile(spec as OperationSpec);
    const newBlock = new TransactionBlock(operations);
    this.setupBlockListeners(newBlock);
    this.transactionBlocks.push(newBlock);
    this.emit('blockAdded', newBlock);
    this.processQueue();
    return operationId;
  }

  private async commitRequest(requestId: string): Promise<void> {
    const { basis } = await SessionService.getBasis();
    console.log('Basis:', basis);
    const commitResponse = await CommitService.commitSingleRequest(
      basis,
      requestId
    );
    console.log('Commit response:', commitResponse);
  }

  async processQueue(): Promise<void> {
    if (this.isProcessing || this.transactionBlocks.length === 0) {
      return;
    }

    this.isProcessing = true;

    for (const currentBlock of this.transactionBlocks) {
      if (currentBlock.isCompleted() || currentBlock.isAborted()) {
        continue;
      }

      this.emit('blockUpdated', currentBlock);
      currentBlock.start();

      for (const operation of currentBlock.operations) {
        const context = new OperationContext();

        try {
          operation.setStatus('running');
          await operation.defineRequest(context);
          await operation.execute(context);

          operation.setStatus('completed');

          if (operation.operationId) {
            console.log('Committing request:', operation.operationId);
            const isDownload =
              context.declareRequestSpec?.operationType === 'file.download' ||
              context.declareRequestSpec?.operationType === 'inbox.download'
            if (isDownload) {
              console.log('Download operation, skipping commit');
              await this.commitRequest('');
            } else {
              await this.commitRequest(operation.operationId);
            }
          }

          this.emit('operationComplete', operation);
          this.emit(`operationComplete:${operation.operationId}`, operation);
        } catch (error) {
          operation.setStatus('error');
          this.emit('operationError', { operation, error });
          this.emit(`operationError:${operation.operationId}`, {
            operation,
            error,
          });
          currentBlock.abort();
          break;
        }

        // Emetti l'evento di aggiornamento de l blocco dopo ogni operazione
        this.emit('blockUpdated', currentBlock);
      }

      if (!currentBlock.isAborted()) {
        currentBlock.complete();
      }

      this.emit('blockCompleted', currentBlock);
    }

    this.isProcessing = false;
  }

  getTransactionBlocks(): TransactionBlock[] {
    return this.transactionBlocks;
  }

  // Nuovo metodo per rimuovere un blocco specifico
  removeTransactionBlock(blockId: string): void {
    const blockIndex = this.transactionBlocks.findIndex(
      (block) => block.id === blockId
    );
    if (blockIndex !== -1) {
      const block = this.transactionBlocks[blockIndex];
      if (block.status !== 'completed') {
        block.abort();
      }
      this.transactionBlocks.splice(blockIndex, 1);
      this.emit('blockRemoved', blockId);
    }
  }

  // Nuovo metodo per rimuovere blocchi per stato
  removeTransactionBlocksByStatus(status: OperationStatus): void {
    const blocksToRemove = this.transactionBlocks.filter(
      (block) => block.status === status
    );

    // Prima abortiamo tutte le operazioni necessarie
    blocksToRemove.forEach((block) => {
      if (block.status !== 'completed' && block.status !== 'aborted') {
        block.abort();
      }
    });

    // Poi rimuoviamo i blocchi e notifichiamo
    this.transactionBlocks = this.transactionBlocks.filter(
      (block) => !blocksToRemove.includes(block)
    );

    // Emettiamo gli eventi di rimozione
    blocksToRemove.forEach((block) => {
      this.emit('blockRemoved', block.id);
    });
  }

  // Nuovo metodo per rimuovere tutti i blocchi
  clearTransactionBlocks(): void {
    // Prima abortiamo tutte le operazioni necessarie
    this.transactionBlocks.forEach((block) => {
      if (block.status !== 'completed' && block.status !== 'aborted') {
        block.abort();
      }
    });

    // Salviamo i blocchi da rimuovere per emettere gli eventi
    const blocksToRemove = [...this.transactionBlocks];

    // Puliamo l'array
    this.transactionBlocks = [];

    // Emettiamo gli eventi di rimozione per ogni blocco
    blocksToRemove.forEach((block) => {
      this.emit('blockRemoved', block.id);
    });

    // Emettiamo l'evento di pulizia completa
    this.emit('blocksCleared');
  }

  private generateOperationId(): string {
    return Math.floor(Math.random() * 1000000000).toString();
  }

  on(event: string, listener: (...args: any[]) => void): void {
    super.on(event, listener);
  }

  once(event: string, listener: (...args: any[]) => void): void {
    const onceWrapper = (...args: any[]) => {
      super.off(event, onceWrapper);
      listener(...args);
    };
    super.on(event, onceWrapper);
  }

  private setupBlockListeners(block: TransactionBlock): void {
    block.on('progressUpdated', (progress: number) => {
      this.emit('blockProgressUpdated', block.id, progress);
    });

    block.on('statusUpdated', (status: OperationStatus) => {
      this.emit('blockStatusUpdated', block.id, status);
    });

    block.on('completed', () => {
      this.emit('blockCompleted', block);
    });

    block.on('aborted', () => {
      this.emit('blockAborted', block);
    });
  }
}
